import type { FileViewOverride } from "@/shared/components/DatasetFileGrid/DatasetGridItem";
import { type FileRecord } from "@/shared/domain/files";
import { FileSystemNode } from "@/shared/domain/files/filesystem";
import { assetSourceToIngest } from "@/shared/domain/urdf";

/**
 * The grid tile's "someone else opens this node" claim, for ingestable sources.
 *
 * The grid is shared and knows nothing about robot descriptions or point cloud
 * scans, so the feature answers its "does anyone want to open this?" question
 * here. Extracted from the component rather than inlined in a `useMemo` so the
 * claim can be asserted directly: the tile is otherwise only reachable through
 * a mounted grid, and the one thing worth pinning down is *which* files it
 * takes over.
 *
 * @param onIngestAsAsset the wizard opener, or `undefined` when the release
 *   flag withholds it — in which case there is no override at all and the grid
 *   opens every node the ordinary way.
 */
export function assetFileViewOverride(
  onIngestAsAsset: ((file: FileRecord) => void) | undefined,
): FileViewOverride | undefined {
  if (onIngestAsAsset === undefined) {
    return undefined;
  }
  return {
    accepts: (node: FileSystemNode) => assetSourceToIngest(node) !== null,
    view: (node: FileSystemNode) => {
      const toIngest = assetSourceToIngest(node);
      if (toIngest !== null) {
        onIngestAsAsset(toIngest);
      }
    },
  };
}
